import {
  findById as findMultiTenantNetwork,
  getCanAccessMultiTenantOrganizationGroups,
  getCurrentMtoHubNetwork,
  getCurrentMtoNetwork,
  getIsCurrentNetworkSpoke,
  getIsMtoAvailable,
  getIsMtoEnabled,
  getIsPermalinkDeeplinkEnabled,
  getMultiTenantMemberNetworksCount,
  getMultiTenantNetworks,
  getMultiTenantNetworksCount,
  getMultiTenantOrganization,
  getMultiTenantOrganizationStatus,
} from '@ms/yammer-data/dist/domains/multiTenantOrganization/selectors';
import type { MultiTenantOrganizationNetwork } from '@ms/yammer-data/dist/state/types';

import { useSelector } from '../useSelector';

type UseGetMultiTenantNetworks = () => ReturnType<typeof getMultiTenantNetworks>;
/**
 * @deprecated PERFORMANCE The underlying selector returns a brand new array instance of entity objects on every call!
 * The underlying selector should be deleted and this hook should be replaced by a `useMultiTenantNetworkIds` hook.
 * Then consumers can pass each id to a child component which can call `useMultiTenantOrganizationNetworkField` for
 * the specific fields it requires, and can manage its own memoization to avoid unnecessary re-renders.
 */
export const useGetMultiTenantNetworks: UseGetMultiTenantNetworks = () =>
  useSelector((state) => getMultiTenantNetworks(state));

type UseFindMultiTenantNetwork = (
  networkId: MultiTenantOrganizationNetwork['id']
) => ReturnType<typeof findMultiTenantNetwork>;
/**
 * @deprecated PERFORMANCE This returns an entity object which will be replaced with a new instance and
 * force re-renders whenever an API call updates the entity state. This should be replaced by a
 * `useMultiTenantOrganizationNetworkField` selector.
 */
export const useFindMultiTenantNetwork: UseFindMultiTenantNetwork = (networkId) =>
  useSelector((state) => findMultiTenantNetwork(state, networkId));

type UseCurrentMtoHubNetwork = () => ReturnType<typeof getCurrentMtoHubNetwork>;
/**
 * @deprecated PERFORMANCE This returns an entity object which will be replaced with a new instance and
 * force re-renders whenever an API call updates the entity state. This should be replaced by a
 * `useCurrentMtoHubNetworkField` selector.
 */
export const useCurrentMtoHubNetwork: UseCurrentMtoHubNetwork = () =>
  useSelector((state) => getCurrentMtoHubNetwork(state));

type UseCurrentMtoNetwork = () => ReturnType<typeof getCurrentMtoNetwork>;
/**
 * @deprecated PERFORMANCE This returns an entity object which will be replaced with a new instance and
 * force re-renders whenever an API call updates the entity state. This should be replaced by a
 * `useCurrentMtoNetworkField` selector.
 */
export const useCurrentMtoNetwork: UseCurrentMtoNetwork = () => useSelector((state) => getCurrentMtoNetwork(state));

type UseGetMultiTenantNetworkOrganization = () => ReturnType<typeof getMultiTenantOrganization>;
/**
 * @deprecated PERFORMANCE This returns an entity object which will be replaced with a new instance and
 * force re-renders whenever an API call updates the entity state. This should be replaced by a
 * `useMultiTenantOrganizationField` selector.
 */
export const useGetMultiTenantNetworkOrganization: UseGetMultiTenantNetworkOrganization = () =>
  useSelector((state) => getMultiTenantOrganization(state));

type UseGetMultiTenantNetworkOrganizationStatus = () => ReturnType<typeof getMultiTenantOrganizationStatus>;
export const useGetMultiTenantNetworkOrganizationStatus: UseGetMultiTenantNetworkOrganizationStatus = () =>
  useSelector((state) => getMultiTenantOrganizationStatus(state));

type UseMtoEnabled = () => boolean;
export const useMtoEnabled: UseMtoEnabled = () => useSelector((state) => getIsMtoEnabled(state));

type UsePermalinkDeeplinkEnabled = () => boolean;
export const usePermalinkDeeplinkEnabled: UsePermalinkDeeplinkEnabled = () =>
  useSelector((state) => getIsPermalinkDeeplinkEnabled(state));

type UseMtoEnabledAndAvailable = () => boolean;
export const useMtoEnabledAndAvailable: UseMtoEnabledAndAvailable = () => {
  const isMtoAvailable = useSelector((state) => getIsMtoAvailable(state));
  const isMtoEnabled = useMtoEnabled();

  return isMtoAvailable && isMtoEnabled;
};

type UseMultiTenantNetworksCount = () => ReturnType<typeof getMultiTenantNetworksCount>;
export const useMultiTenantNetworksCount: UseMultiTenantNetworksCount = () =>
  useSelector((state) => getMultiTenantNetworksCount(state));

type UseMultiTenantMemberNetworksCount = () => ReturnType<typeof getMultiTenantMemberNetworksCount>;
export const useMultiTenantMemberNetworksCount: UseMultiTenantMemberNetworksCount = () =>
  useSelector((state) => getMultiTenantMemberNetworksCount(state));

type UseIsCurrentNetworkSpoke = () => ReturnType<typeof getIsCurrentNetworkSpoke>;
export const useIsCurrentNetworkSpoke: UseIsCurrentNetworkSpoke = () =>
  useSelector((state) => getIsCurrentNetworkSpoke(state));

type UseCanAccessMultiTenantOrganizationGroups = () => ReturnType<typeof getCanAccessMultiTenantOrganizationGroups>;
export const useCanAccessMultiTenantOrganizationGroups: UseCanAccessMultiTenantOrganizationGroups = () =>
  useSelector((state) => getCanAccessMultiTenantOrganizationGroups(state));
